import React from 'react';

import { lazyRetry } from '@utils/lazyRetry';
import JwtAuthProvider from '@auth/services/jwt/JwtAuthProvider';
import { FuseAuthProviderType } from '@fuse/core/FuseAuthProvider/types/FuseAuthTypes';
import FuseAuthProvider from '@fuse/core/FuseAuthProvider';
import FuseAuthorization from '@fuse/core/FuseAuthorization';
import { User } from '@auth/user';

// The AWS Cognito (Amplify) provider is registered only when AWS is actually
// configured via VITE_AWS_* env vars. Importing it eagerly pulled the entire Amplify
// SDK (aws-amplify + @aws-amplify/ui-react + @aws-amplify/auth, and transitively
// xstate + qrcode) into the app entry chunk on EVERY page — but deployments
// authenticate with JWT and ship no Cognito config, so it was pure dead weight.
// Lazy + conditional keeps Amplify out of the bundle for JWT-only deployments.
const AWSAuthProvider = lazyRetry(() => import('@auth/services/aws/AWSAuthProvider'));

/**
 * The Authentication providers.
 */
const authProviders: FuseAuthProviderType[] = [
  {
    name: 'jwt',
    Provider: JwtAuthProvider
  },
  ...(import.meta.env.VITE_AWS_USER_POOLS_ID
    ? [{ name: 'aws', Provider: AWSAuthProvider } as FuseAuthProviderType]
    : [])
];

type AuthenticationProps = {
  children: React.ReactNode;
};

function Authentication(props: AuthenticationProps) {
  const { children } = props;

  return (
    <FuseAuthProvider providers={authProviders}>
      {(authState) => {
        const userRole = authState?.user?.role as User['role'];
        return <FuseAuthorization userRole={userRole}>{children}</FuseAuthorization>;
      }}
    </FuseAuthProvider>
  );
}

export default Authentication;
